import { assign, createMachine } from 'xstate'
import { Context, Event, EventId, Options, Props, State, StateId } from './Types'
import { useMachine } from '@xstate/react'
import { usePrepareBatchCampaigns } from 'hooks/queries/schedules/usePrepareBatchCampaigns'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCreateSchedules } from 'hooks/queries/brandPlans/useCreateSchedules'
import { useModals } from 'almanac-ui/Modal'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { generatePath } from 'react-router-dom'
import { BRAND_KITS_PATH } from 'paths'
import { ApiError } from 'utils/error'

const createBatchCampaignsMachine = createMachine<Context, Event, State>({
  id: 'createBatchCampaigns',
  initial: StateId.CreateBatch,
  on: {
    [EventId.PrepareBatch]: {
      target: StateId.Analysing,
      actions: [
        assign({
          topics: (context, event) => event.topics,
          start_date: (context, event) => event.start_date,
          sources: (context, event) => event.sources,
          media: (context, event) => event.media,
        }),
      ],
    },
  },
  states: {
    [StateId.CreateBatch]: {
      meta: {
        title: 'Create New Campaign',
      },
      on: {
        [EventId.Back]: {
          actions: 'close',
        },
      },
    },
    [StateId.Analysing]: {
      entry: 'prepareBatch',
      exit: assign({
        transient: (context) => {
          context.transient.abortController.abort()
          return {
            ...context.transient,
            abortController: new AbortController(),
          }
        },
      }),
      meta: {
        title: 'Reviewing',
      },
      on: {
        [EventId.Back]: StateId.CreateBatch,
        [EventId.PreparationComplete]: {
          target: StateId.Review,
          actions: assign({
            campaigns: (context, event) => event.preparation.result || [],
          }),
        },
        [EventId.Error]: {
          target: StateId.CreateBatch,
          actions: assign({
            transient: (context, event) => ({ ...context.transient, error: event.error }),
          }),
        },
      },
    },
    [StateId.Review]: {
      meta: {
        title: 'Review Your Campaign Details',
      },
      on: {
        [EventId.Back]: StateId.CreateBatch,
        [EventId.Finish]: {
          actions: [
            assign({
              campaigns: (_context, event) => event.campaigns,
            }),
            'createCampaigns',
          ],
        },
      },
    },
  },
})

export const useCreateBatchCampaignsMachine = (props: Props, options: Options) => {
  const { brandPlanId } = props
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { closeAllModals } = useModals()
  const { mutate: prepareBatch } = usePrepareBatchCampaigns(
    { brandPlanId },
    {
      onSuccess: (data) => {
        send({ type: EventId.PreparationComplete, preparation: data })
      },
      onError: (error) => {
        send({ type: EventId.Error, error })
      },
    },
  )
  const { mutate: createCampaigns } = useCreateSchedules(
    { brandPlanId },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['workspaces', workspaceId, 'schedules'])
        closeAllModals()
      },
      onError: (error) => {
        if (error instanceof ApiError && error.errorCode === 'no_brand_kit_media') {
          const brandKitPath = generatePath(BRAND_KITS_PATH, { workspaceId }) + '#images_videos'
          showToastMessage(
            'Your content preferences require brand kit media, but none are uploaded.',
            'error',
            { type: 'link', to: brandKitPath, label: 'Go to Brand Kit', variant: 'proceed' },
            10,
          )
        } else {
          showErrorMessageToast('There was a problem creating your campaigns. Please try again.')
        }
        send({ type: EventId.Error, error })
      },
    },
  )

  const machine = useMachine<Context, Event, State>(createBatchCampaignsMachine, {
    ...options,
    actions: {
      ...options.actions,
      prepareBatch: async (ctx) => {
        prepareBatch({
          start_date: ctx.start_date,
          campaigns: Array.from({ length: ctx.number_of_campaigns }).map((_, index) => {
            return {
              topic: ctx.topics[index % ctx.topics.length],
              sources: ctx.sources,
              media_files: ctx.media.map(file => ({ id: file.media_file_id as number })),
            }
          }),
          signal: ctx.transient.abortController.signal,
        })
      },
      createCampaigns: (ctx) => {
        const { campaigns, sources, media } = ctx
        createCampaigns(campaigns.map(campaign => ({
          ...campaign,
          sources,
          media_files: media.map(file => ({ id: file.media_file_id as number }))
        })))
      },
    },
  })

  const [, send] = machine

  return machine
}
